import { Controller } from "@hotwired/stimulus"
import flatpickr from "flatpickr"

export default class extends Controller {
  static targets = ["input", "hidden"]
  static values = {
    mode: { type: String, default: "single" },
    dateFormat: { type: String, default: "M j, Y" },
    defaultDate: { type: Array, default: [] },
    minDate: { type: String, default: "" },
    maxDate: { type: String, default: "" },
    disabled: { type: Boolean, default: false }
  }

  connect() {
    this.initFlatpickr()
  }

  disconnect() {
    if (this.picker) {
      this.picker.destroy()
    }
  }

  initFlatpickr() {
    const options = {
      mode: this.modeValue,
      static: true,
      monthSelectorType: "static",
      dateFormat: this.dateFormatValue,
      prevArrow: '<svg class="fill-current" width="7" height="11" viewBox="0 0 7 11"><path d="M5.4 10.8l1.4-1.4-4-4 4-4L5.4 0 0 5.4z" /></svg>',
      nextArrow: '<svg class="fill-current" width="7" height="11" viewBox="0 0 7 11"><path d="M1.4 10.8L0 9.4l4-4-4-4L1.4 0l5.4 5.4z" /></svg>',
      onReady: (selectedDates, dateStr, instance) => {
        if (this.modeValue === "range") {
          instance.element.value = dateStr.replace("to", "-")
        }
        const customClass = instance.element.getAttribute("data-class")
        if (customClass) {
          instance.calendarContainer.classList.add(customClass)
        }
        this.updateHiddenInput(selectedDates)
      },
      onChange: (selectedDates, dateStr, instance) => {
        if (this.modeValue === "range") {
          instance.element.value = dateStr.replace("to", "-")
        }
        this.updateHiddenInput(selectedDates)
        this.dispatch("change", { detail: { dates: selectedDates, dateStr } })
      }
    }

    // Set default date
    if (this.hasDefaultDateValue && this.defaultDateValue.length > 0) {
      options.defaultDate = this.defaultDateValue
    } else if (this.modeValue === "range") {
      // Default to last 7 days for range mode
      const today = new Date()
      const weekAgo = new Date()
      weekAgo.setDate(today.getDate() - 6)
      options.defaultDate = [weekAgo, today]
    }

    // Set min/max dates - parse as Date objects for reliable constraint enforcement
    if (this.minDateValue && this.minDateValue.length > 0) {
      options.minDate = this.parseDate(this.minDateValue)
    }
    if (this.maxDateValue && this.maxDateValue.length > 0) {
      options.maxDate = this.parseDate(this.maxDateValue)
    }

    this.picker = flatpickr(this.inputTarget, options)

    if (this.disabledValue) {
      this.disable()
    }
  }

  updateHiddenInput(selectedDates) {
    if (this.hasHiddenTarget) {
      if (this.modeValue === "range" && selectedDates.length === 2) {
        this.hiddenTarget.value = JSON.stringify(selectedDates.map(d => d.toISOString().split('T')[0]))
      } else if (selectedDates.length > 0) {
        this.hiddenTarget.value = selectedDates[0].toISOString().split('T')[0]
      } else {
        this.hiddenTarget.value = ""
      }
    }
  }

  // Public API methods
  setDate(date) {
    if (this.picker) {
      this.picker.setDate(date, true)
    }
  }

  clear() {
    if (this.picker) {
      this.picker.clear()
    }
  }

  open() {
    if (this.picker) {
      this.picker.open()
    }
  }

  close() {
    if (this.picker) {
      this.picker.close()
    }
  }

  getValue() {
    if (this.picker) {
      return this.picker.selectedDates
    }
    return []
  }

  enable() {
    if (this.picker) {
      this.inputTarget.disabled = false
    }
  }

  disable() {
    if (this.picker) {
      this.inputTarget.disabled = true
    }
  }

  parseDate(dateString) {
    // Parse YYYY-MM-DD format to Date object
    // Using UTC to avoid timezone issues
    const [year, month, day] = dateString.split('-').map(Number)
    return new Date(year, month - 1, day)
  }
}
